import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { AnimatedCircularProgressBar } from './magicui/animated-circular-progress-bar';
import { Button } from './ui/Button';
import { useProcessingStatus } from '../hooks/useProcessingStatus';
import { CheckCircle, FileText, AlertTriangle, RefreshCw } from 'lucide-react';

interface ProcessingModalProps {
  isOpen: boolean;
  onClose: () => void;
  fileName?: string;
  sessionId?: string;
}

const ProcessingModal: React.FC<ProcessingModalProps> = ({
  isOpen,
  onClose,
  fileName = 'your file',
  sessionId
}) => {
  const [progress, setProgress] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const navigate = useNavigate();
  const { status: processing, error } = useProcessingStatus(sessionId);

  // Handle progress updates based on backend status
  useEffect(() => {
    if (!isOpen || !processing) return;

    if (processing.status === 'completed') {
      setProgress(100);
      setIsCompleted(true);
      return;
    }

    if (processing.status === 'error') {
      setProgress(0);
      return;
    }

    // Use real-time progress from backend
    const backendProgress = processing.progress || 0;
    
    // Smooth animation to backend progress value
    const interval = setInterval(() => {
      setProgress(prev => {
        const diff = backendProgress - prev;
        if (Math.abs(diff) < 0.5) {
          clearInterval(interval);
          return backendProgress;
        }
        // Smooth transition to backend progress
        return prev + (diff * 0.1);
      });
    }, 100);

    return () => clearInterval(interval);
  }, [isOpen, processing, processing?.progress, processing?.status]);

  const handleNavigateToDashboard = () => {
    onClose();
    navigate('/');
  };

  const handleNavigateToTalents = () => {
    onClose();
    navigate('/talents');
  };

  const handleViewTalentCard = () => {
    onClose();
    navigate(`/talents/${sessionId}`);
  };

  const getProgressText = () => {
    if (isCompleted) return 'Analysis complete!';
    if (error) {
      if (error.includes('session may have expired') || error.includes('server was restarted')) {
        return 'Please upload your file again to restart processing';
      }
      return 'Please try again or contact support';
    }
    if (processing?.status === 'error') return 'Please try again';
    if (processing?.status === 'processing') {
      return processing.currentStage || 'Processing your file...';
    }
    return 'This may take a few minutes';
  };

  const getDisplayFileName = () => {
    // Use filename from API response if available, otherwise fallback to fileName prop
    return processing?.filename || fileName || 'your file';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center flex items-center justify-center gap-2">
            {isCompleted ? (
              <CheckCircle className="w-5 h-5 text-[var(--success-color)]" />
            ) : error && (error.includes('session may have expired') || error.includes('server was restarted')) ? (
              <AlertTriangle className="w-5 h-5 text-orange-500" />
            ) : error ? (
              <AlertTriangle className="w-5 h-5 text-[var(--error-color)]" />
            ) : (
              <FileText className="w-5 h-5 text-[var(--info-color)]" />
            )}
            {isCompleted ? 'Processing Complete!' : 
             error && (error.includes('session may have expired') || error.includes('server was restarted')) ? 'Session Expired' :
             error ? 'Processing Error' : 
             `Processing ${getDisplayFileName()}`}
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex flex-col items-center space-y-6 py-6">
          <AnimatedCircularProgressBar
            value={progress}
            gaugePrimaryColor={isCompleted ? "rgb(34 197 94)" : "rgb(79 70 229)"}
            gaugeSecondaryColor="rgba(0, 0, 0, 0.1)"
          />
          
          <div className="text-center space-y-2">
            <p className="text-[var(--font-size-sm)] font-[var(--font-weight-medium)]">
              {isCompleted ? 'Ready to view!' : 'Processing in progress...'}
            </p>
            <p className="text-[var(--font-size-xs)] text-[var(--text-tertiary)]">
              {getProgressText()}
            </p>
          </div>
        </div>

        <div className="border-t pt-4">
          {isCompleted ? (
            <div className="space-y-4">
              <p className="text-[var(--font-size-sm)] text-center text-[var(--success-color)] font-[var(--font-weight-medium)]">
                Your talent card is ready for review!
              </p>
              <div className="flex gap-2 justify-center">
                <Button 
                  onClick={handleViewTalentCard}
                  className="bg-green-600 hover:bg-green-700"
                >
                  View Talent Card
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleNavigateToDashboard}
                >
                  Dashboard
                </Button>
              </div>
            </div>
          ) : error && (error.includes('session may have expired') || error.includes('server was restarted')) ? (
            <div className="space-y-4">
              <p className="text-[var(--font-size-sm)] text-center text-orange-600 font-[var(--font-weight-medium)]">
                Your processing session has expired. This can happen when the server restarts.
              </p>
              <div className="flex gap-2 justify-center">
                <Button 
                  onClick={() => {
                    onClose();
                    navigate('/upload');
                  }}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Upload Again
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleNavigateToDashboard}
                >
                  Dashboard
                </Button>
              </div>
            </div>
          ) : error ? (
            <div className="space-y-4">
              <p className="text-[var(--font-size-sm)] text-center text-[var(--error-color)] font-[var(--font-weight-medium)]">
                An error occurred during processing. Please try again.
              </p>
              <div className="flex gap-2 justify-center">
                <Button 
                  onClick={() => {
                    onClose();
                    navigate('/upload');
                  }}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Try Again
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleNavigateToDashboard}
                >
                  Dashboard
                </Button>
              </div>
            </div>
          ) : (
            <>
              <p className="text-[var(--font-size-xs)] text-[var(--text-tertiary)] text-center mb-4">
                Processing is happening in the background. You can safely navigate to other pages.
              </p>
              
              <div className="flex gap-2 justify-center">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleNavigateToDashboard}
                >
                  Go to Dashboard
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleNavigateToTalents}
                >
                  View Talents
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProcessingModal;